import { useCallback, useEffect, useMemo, useState } from 'react';

export function useSavedMarketplaceListings({ apiUrl, getAccessTokenSilently, enabled = true }) {
  const [savedListings, setSavedListings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingAssetId, setSavingAssetId] = useState(null);
  const [error, setError] = useState(null);

  const savedAssetIds = useMemo(
    () => new Set(savedListings.map((saved) => Number(saved.assetId))),
    [savedListings],
  );

  const refreshSavedListings = useCallback(async () => {
    if (!enabled || !apiUrl || !getAccessTokenSilently) {
      setSavedListings([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${apiUrl}/api/MarketplaceSavedListings`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error(`Saved listing load failed (${response.status})`);
      }

      const data = await response.json();
      setSavedListings(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading saved marketplace listings:', err);
      setError('Could not load your saved listings. Please retry.');
      setSavedListings([]);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, enabled, getAccessTokenSilently]);

  useEffect(() => {
    refreshSavedListings();
  }, [refreshSavedListings]);

  const toggleSavedListing = useCallback(async (assetId) => {
    if (!apiUrl || !getAccessTokenSilently || !assetId) {
      return;
    }

    const numericAssetId = Number(assetId);
    const isCurrentlySaved = savedAssetIds.has(numericAssetId);
    setSavingAssetId(numericAssetId);
    setError(null);

    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${apiUrl}/api/MarketplaceSavedListings/${numericAssetId}`, {
        method: isCurrentlySaved ? 'DELETE' : 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error(`Saved listing update failed (${response.status})`);
      }

      setSavedListings((current) => {
        if (isCurrentlySaved) {
          return current.filter((saved) => Number(saved.assetId) !== numericAssetId);
        }

        return [
          { assetId: numericAssetId, savedAt: new Date().toISOString() },
          ...current.filter((saved) => Number(saved.assetId) !== numericAssetId),
        ];
      });
    } catch (err) {
      console.error('Error updating saved marketplace listing:', err);
      setError('Could not update this saved listing. Please retry.');
    } finally {
      setSavingAssetId(null);
    }
  }, [apiUrl, getAccessTokenSilently, savedAssetIds]);

  return {
    savedListings,
    savedAssetIds,
    loading,
    savingAssetId,
    error,
    refreshSavedListings,
    toggleSavedListing,
  };
}

export default useSavedMarketplaceListings;
